// @ts-ignore
import environment from '../environment.json';

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import AuthService from '../services/AuthService';
import AsyncAuthService from '../services/AsyncAuthService';
import EnvironmentService from '../services/EnvironmentService';
import ApiResponseV2 from '../core/ApiResponseV2';
import { ApiResultDTO } from '../core/ApiResultDTO';
import NotificationService from '../services/NotificationService';

export interface HttpClientV2Config {
  /**
   * Used to override axios default config
   */
  axiosConfig?: AxiosRequestConfig;
}

/**
 * An HTTP client wrapper around Axios.
 *
 * @param baseURL - The base URL for all API requests.
 * @param requestTimeout - The timeout in milliseconds for all requests, 5000 ms by default
 */
export class HttpClientV2 {
  private client: AxiosInstance;
  private isHandlingUnauthorized = false;

  private readonly sessionExpiredMessage = 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.';

  // Backend signal for "the credentials you sent were rejected (expired/revoked/invalid)".
  // Sent as a header and as the body error code. Any other 401 is a non-session error
  // (e.g. bad login credentials, missing authorization) — the caller handles those.
  private readonly invalidTokenAuthCode = 'INVALID_TOKEN';

  /**
   * Constructs a new `HttpClientV2`.
   *
   * @param {string} baseURL - The base URL for all API requests.
   * @param {number} [requestTimeout=5000] - The timeout in milliseconds for all requests.
   */
  constructor(baseURL: string, requestTimeout?: number) {
    this.client = axios.create({
      baseURL: baseURL,
      timeout: requestTimeout || 5000,
    });

    this.generateConfig().then((config) => {
      if (!!config.headers?.Authorization) {
        axios.defaults.headers.common['Authorization'] = config.headers!.Authorization;
      }
    });
    this.client.interceptors.response.use((response) => response, (error) => this.handleUnauthorized(error));
  }

  private async handleUnauthorized(error: any) {
    const responseStatus = error?.response?.status;

    if (responseStatus !== 401) {
      return Promise.reject(error);
    }

    // Only redirect when the backend explicitly signals that authentication is required
    // (INVALID_TOKEN). Any other 401 (bad login credentials from the auth controller,
    // endpoint-level authorization failure, etc.) is for the caller to handle.
    if (!this.isInvalidTokenResponse(error?.response)) {
      return Promise.reject(error);
    }

    // Already on /login — don't redirect in place, just propagate.
    if (typeof window !== 'undefined' && window.location.pathname === '/login') {
      return Promise.reject(error);
    }

    if (this.isHandlingUnauthorized) {
      return Promise.reject(error);
    }

    this.isHandlingUnauthorized = true;

    // Set session_expired flag so the login page can show the "session expired" banner
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('session_expired', 'true');
      sessionStorage.setItem('postLoginRedirect', window.location.href);
    }

    if (EnvironmentService.usesAsyncStorageStrategy()) {
      AsyncAuthService.logOut();
    } else {
      AuthService.logOut(); // LOGOUT event → useSessionManager handleLogout → navigates to login
    }

    return Promise.reject(error);
  }

  private isInvalidTokenResponse(response: AxiosResponse | undefined): boolean {
    if (!response) return false;

    const headerValue = response.headers?.['x-auth-code'] ?? response.headers?.['X-AUTH-CODE'];
    if (headerValue === this.invalidTokenAuthCode) return true;

    const bodyErrorCode = (response.data as { error?: string } | undefined)?.error;
    return bodyErrorCode === this.invalidTokenAuthCode;
  }

  /**
   * Adds an `Authorization` header to the given `AxiosRequestConfig` object.
   *
   * @private
   * @param {AxiosRequestConfig} config - The configuration object to add the header to.
   * @param {string} token - The authorization token to include in the header.
   */
  private addBaseConfig(config: AxiosRequestConfig, token: any) {
    if (token) {
      if (!config.headers) {
        config.headers = {};
      }
      config.headers['Authorization'] = `Bearer ${token}`;
    }
  }

  /**
   * Generates a new `AxiosRequestConfig` object with the appropriate headers.
   *
   * @private
   * @param {AxiosRequestConfig} [extraConfig] - Additional configuration options to include.
   * @returns {Promise<AxiosRequestConfig>} A promise that resolves to the generated configuration object.
   */
  private async generateConfig(extraConfig?: AxiosRequestConfig): Promise<AxiosRequestConfig> {
    var config: AxiosRequestConfig = extraConfig || {};

    if (EnvironmentService.usesAsyncStorageStrategy()) {
      this.addBaseConfig(config, await AsyncAuthService.getToken());
    } else {
      this.addBaseConfig(config, AuthService.getToken());
    }

    return config;
  }

  /**
   * Handles an error that occurred during an Axios request.
   *
   * @private
   * @template T
   * @param {Object} error - The Axios error object.
   * @param {AxiosResponse<ApiResultDTO<T>> | undefined} error.response - The Axios response object, if available.
   * @param {XMLHttpRequest | undefined} error.request - The XMLHttpRequest or http.ClientRequest object, if available.
   * @returns {ApiResponseV2<T>} An ApiResponseV2 object representing the error.
   */
  private handleAxiosRequestError<T>(error: {
    response: AxiosResponse<ApiResultDTO<T>> | undefined;
    request: XMLHttpRequest | undefined;
  }): ApiResponseV2<T> {
    if (error.response) {
      // The request was made and the server responded with a status code that falls out of the range of 2xx
      return ApiResponseV2.fromAxiosResponse(error.response);
    }

    if (error.request) {
      // The request was made but no response was received
      // `error.request` is an instance of XMLHttpRequest in the browser and an instance of http.ClientRequest in node.js
      return ApiResponseV2.noResponseError();
    }

    // Something happened in setting up the request that triggered an Error
    return ApiResponseV2.requestError();
  }

  /**
   * Sends a GET request to the API.
   *
   * @async
   * @template T
   * @param {string} url - The URL for the request.
   * @param {HttpClientV2Config} [config] - Additional configuration options for the request.
   * @returns {Promise<ApiResponseV2<T>>} A promise that resolves to an ApiResponseV2 object.
   */
  async get<T>(url: string, config?: HttpClientV2Config): Promise<ApiResponseV2<T>> {
    return this.client
      .get(url, await this.generateConfig(config?.axiosConfig))
      .then((response: AxiosResponse<ApiResultDTO<T>>) => ApiResponseV2.fromAxiosResponse(response))
      .catch((error) => this.handleAxiosRequestError(error));
  }

  /**
   * Performs an HTTP POST request with the given payload.
   *
   * @template T The expected response data type.
   * @template R The type of the payload data.
   * @param {string} url The URL to send the request to.
   * @param {R} payload The payload to send with the request.
   * @param {HttpClientV2Config} [config] Optional configuration overrides.
   * @returns {Promise<ApiResponseV2<R>>} A Promise that resolves with the response data.
   */
  async post<T, R>(url: string, payload: T, config?: HttpClientV2Config): Promise<ApiResponseV2<R>> {
    return this.client
      .post(url, payload, await this.generateConfig(config?.axiosConfig))
      .then((response: AxiosResponse<ApiResultDTO<R>>) => ApiResponseV2.fromAxiosResponse(response))
      .catch((error) => this.handleAxiosRequestError(error));
  }

  /**
   * Performs an HTTP PUT request with the given payload.
   *
   * @template T The expected response data type.
   * @template R The type of the payload data.
   * @param {string} url The URL to send the request to.
   * @param {R} payload The payload to send with the request.
   * @param {HttpClientV2Config} [config] Optional configuration overrides.
   * @returns {Promise<ApiResponseV2<R>>} A Promise that resolves with the response data.
   */
  async put<T, R>(url: string, payload: T, config?: HttpClientV2Config): Promise<ApiResponseV2<R>> {
    return this.client
      .put(url, payload, await this.generateConfig(config?.axiosConfig))
      .then((response: AxiosResponse<ApiResultDTO<R>>) => ApiResponseV2.fromAxiosResponse(response))
      .catch((error) => this.handleAxiosRequestError(error));
  }

  /**
   * Performs an HTTP PUT request with form data.
   *
   * @template R The expected response data type.
   * @param {string} url The URL to send the request to.
   * @param {R} body The form data payload to send with the request.
   * @param {HttpClientV2Config} [config] Optional configuration overrides.
   * @returns {Promise<ApiResponseV2<R>>} A Promise that resolves with the response data.
   */
  async putForm<R>(url: string, body: FormData, config?: HttpClientV2Config): Promise<ApiResponseV2<R>> {
    const extraConfig = {
      ...config?.axiosConfig,
      headers: {
        ...config?.axiosConfig?.headers,
        'Content-Type': 'multipart/form-data',
      },
    };

    return this.client
      .put(url, body, await this.generateConfig(extraConfig))
      .then((response: AxiosResponse<ApiResultDTO<R>>) => ApiResponseV2.fromAxiosResponse(response))
      .catch((error) => this.handleAxiosRequestError(error));
  }

  /**
   * Performs an HTTP DELETE request.
   *
   * @template T The expected response data type.
   * @param {string} url The URL to send the request to.
   * @param {HttpClientV2Config} [config] Optional configuration overrides.
   * @returns {Promise<ApiResponseV2<T>>} A Promise that resolves with the response data.
   */
  async delete<T>(url: string, config?: HttpClientV2Config): Promise<ApiResponseV2<T>> {
    return this.client
      .delete(url, await this.generateConfig(config?.axiosConfig))
      .then((response: AxiosResponse<ApiResultDTO<T>>) => ApiResponseV2.fromAxiosResponse(response))
      .catch((error) => this.handleAxiosRequestError(error));
  }

  /**
   * Sends a GET FILE request to the API.
   *
   * @async
   * @template T The expected response data type.
   * @param {string} url - The URL for the request.
   * @param {HttpClientV2Config} [config] - Additional configuration options for the request.
   * @returns {Promise<ApiResponseV2<T>>} A promise that resolves to an ApiResponseV2 object.
   */
  async getFile<T>(url: string, config?: HttpClientV2Config): Promise<ApiResponseV2<T>> {
    const customConfig = await this.generateConfig(config?.axiosConfig);
    customConfig.responseType = 'blob';
    return this.client
      .get(url, customConfig)
      .then((response: AxiosResponse<ApiResultDTO<T>>) => ApiResponseV2.fromAxiosResponse(response))
      .catch((error) => this.handleAxiosRequestError(error));
  }

  /**
   * Performs an HTTP POST request with the given payload.
   *
   * @template T The expected response data type.
   * @template R The type of the payload data.
   * @param {string} url The URL to send the request to.
   * @param {R} payload The payload to send with the request.
   * @param {HttpClientV2Config} [config] Optional configuration overrides.
   * @returns {Promise<ApiResponseV2<R>>} A Promise that resolves with the response data.
   */
  async postFile<T, R>(url: string, payload: R, config?: HttpClientV2Config): Promise<ApiResponseV2<T>> {
    const customConfig = await this.generateConfig();
    customConfig.responseType = 'blob';
    return this.client
      .post(url, payload, customConfig)
      .then((response: AxiosResponse<ApiResultDTO<T>>) => ApiResponseV2.fromAxiosResponse(response))
      .catch((error) => this.handleAxiosRequestError(error));
  }

  /**
   * Performs an HTTP PATCH request with the given payload.
   *
   * @template T The expected response data type.
   * @template R The type of the payload data.
   * @param {string} url The URL to send the request to.
   * @param {R} payload The payload to send with the request.
   * @param {HttpClientV2Config} [config] Optional configuration overrides.
   * @returns {Promise<ApiResponseV2<R>>} A Promise that resolves with the response data.
   */
  async patchWithPayload<T, R>(url: string, payload: T, config?: HttpClientV2Config): Promise<ApiResponseV2<R>> {
    return this.client
      .patch(url, payload, await this.generateConfig(config?.axiosConfig))
      .then((response: AxiosResponse<ApiResultDTO<R>>) => ApiResponseV2.fromAxiosResponse(response))
      .catch((error) => this.handleAxiosRequestError(error));
  }

  /**
   * Performs an HTTP PATCH request without payload.
   *
   * @template T The expected response data type.
   * @param {string} url The URL to send the request to.
   * @returns {Promise<ApiResponseV2<T>>} A Promise that resolves with the response data.
   */
  async patchWithoutPayload<T>(url: string, config?: HttpClientV2Config): Promise<ApiResponseV2<T>> {
    return this.client
      .patch(url, {}, await this.generateConfig(config?.axiosConfig))
      .then((response: AxiosResponse<ApiResultDTO<T>>) => ApiResponseV2.fromAxiosResponse(response))
      .catch((error) => this.handleAxiosRequestError(error));
  }

  getClient(): AxiosInstance {
    return this.client;
  }
}

export default new HttpClientV2(environment.baseURL, environment.requestTimeout);
